import { FunctionComponent, useMemo } from 'react';
import { FaRegBuilding } from 'react-icons/fa';
import { FaChartLine, FaFileLines } from 'react-icons/fa6';
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  AreaChart,
  Area,
} from 'recharts';
import { Tooltip as MuiTooltip } from '@mui/material';
import { AUMTime } from '../types/dashboard.types';

interface TotalFundsProps {
  totalFunds: number;
  totalReportedFunds: number;
  fundsList: AUMTime[]; // reuse same type since it has { date, aum, fund_count }
}

const TotalFundsCard: FunctionComponent<TotalFundsProps> = ({
  totalFunds,
  totalReportedFunds,
  fundsList,
}) => {
  const chartData = useMemo(() => {
    return (fundsList || []).map((d) => ({
      date: d.date,
      funds: d.fund_count,
    }));
  }, [fundsList]);

  const show = fundsList && fundsList.length >= 2;

  return (
    <div className="relative mt-4 min-w-[150px] flex-1 border-t border-gray-200 pt-4 sm:mt-0 sm:h-full sm:border-t-0 sm:pl-6 sm:pt-0">
      {/* Divider line for layout */}
      <div className="sm:absolute sm:left-0 sm:top-0 sm:h-full sm:border-l sm:border-gray-200"></div>

      <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
        <div>
          <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
            <FaRegBuilding className="text-blue-500" />
            Funds
          </div>
          <div className="mt-1.5 text-lg font-bold tracking-tight text-gray-900">
            {totalFunds}
          </div>
        </div>
        <div>
          <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
            <FaFileLines className="text-green-500" />
            Reported
            <span className="font-normal normal-case tracking-normal text-gray-400">
              Within chosen period
            </span>
          </div>
          <div className="mt-1.5 text-lg font-bold tracking-tight text-gray-900">
            {totalReportedFunds}
          </div>
        </div>
      </div>

      {/* Funds Trend Chart (reported funds over time) */}
      <div className="mt-1 h-16">
        {show ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              className="rounded border border-gray-200 bg-white"
            >
              <XAxis dataKey="date" hide />
              <YAxis hide />
              <Tooltip
                formatter={(value: number) => `${value} reported funds`}
                labelFormatter={(label) => `Date: ${label}`}
                position={{ y: 50 }}
              />
              <Area
                dataKey="funds"
                name="Reported Funds"
                stroke="#3b82f6"
                fill="#3b82f6"
                fillOpacity={0.3}
                strokeWidth={2}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <MuiTooltip title="Chart available for data over 1 day" arrow>
            <div className="bg-gray-25 flex h-full items-center justify-center rounded border border-gray-100">
              <FaChartLine className="text-xs text-gray-400" />
            </div>
          </MuiTooltip>
        )}
      </div>
    </div>
  );
};

export default TotalFundsCard;
